/**
 * AdminUsersTab - User management.
 *
 * Lists all users with search, plan filter, and pagination.
 * Allows creating new users with automatic trial subscription.
 * "Detalhes" opens AdminUserDetailModal (support actions + diagnostics).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, ChevronLeft, ChevronRight, Save, X, Copy, UserCog } from 'lucide-react';
import AdminUserDetailModal from '@/components/admin/AdminUserDetailModal';
import {
  getUsers,
  getPlans,
  createUser,
  type AdminUserData,
  type PlanData,
} from '@/services/adminService';

const AdminUsersTab: React.FC = () => {
  const [users, setUsers] = useState<AdminUserData[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create user form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', password: '', displayName: '', planId: '' });
  const [createdPassword, setCreatedPassword] = useState('');
  const [detailUserId, setDetailUserId] = useState<string | null>(null);

  const limit = 25;

  useEffect(() => {
    getPlans().then(({ plans }) => setPlans(plans)).catch(() => {});
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {
        page: String(page),
        limit: String(limit),
      };
      if (search) params.search = search;

      const result = await getUsers(params);
      setUsers(result.users);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setPage(1);
    fetchUsers();
  };

  const handleCreate = async () => {
    setError('');
    setCreatedPassword('');
    try {
      const result = await createUser({
        email: createForm.email,
        password: createForm.password || undefined,
        displayName: createForm.displayName || undefined,
        planId: createForm.planId || undefined,
      });
      if (result.generatedPassword) {
        setCreatedPassword(result.generatedPassword);
      } else {
        setShowCreate(false);
        setCreateForm({ email: '', password: '', displayName: '', planId: '' });
      }
      fetchUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">{error}</div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Usuarios ({total})</h2>
        <Button size="sm" onClick={() => setShowCreate(true)} disabled={showCreate}>
          <Plus className="h-4 w-4 mr-1" /> Criar Usuario
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="border border-border rounded-lg p-4 space-y-3 bg-muted/30">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Email *</label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="user@email.com"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Senha (auto se vazio)</label>
              <Input
                type="text"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Auto-gerada se vazio"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Nome</label>
              <Input
                value={createForm.displayName}
                onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })}
                placeholder="Nome de exibicao"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Plano</label>
              <select
                value={createForm.planId}
                onChange={(e) => setCreateForm({ ...createForm, planId: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Trial (padrao)</option>
                {plans.filter((p) => p.isActive).map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} ({plan.slug})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {createdPassword && (
            <div className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 text-sm p-3 rounded-lg flex items-center gap-2">
              <span>Senha gerada: <code className="font-mono bg-muted px-1.5 py-0.5 rounded">{createdPassword}</code></span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigator.clipboard.writeText(createdPassword)}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          )}

          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreate} disabled={!createForm.email}>
              <Save className="h-4 w-4 mr-1" /> Criar
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowCreate(false);
                setCreatedPassword('');
                setCreateForm({ email: '', password: '', displayName: '', planId: '' });
              }}
            >
              <X className="h-4 w-4 mr-1" /> Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="flex gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Buscar por nome, handle ou email..."
          className="flex-1"
        />
        <Button onClick={handleSearch} disabled={loading}>
          Buscar
        </Button>
      </div>

      {/* Users table */}
      {loading ? (
        <p className="text-muted-foreground">Carregando...</p>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Nome</th>
                <th className="text-left px-4 py-2 font-medium">Handle</th>
                <th className="text-center px-4 py-2 font-medium">Plano</th>
                <th className="text-center px-4 py-2 font-medium">Role</th>
                <th className="text-right px-4 py-2 font-medium">Criado em</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-t border-border">
                  <td className="px-4 py-2 text-xs">{user.email}</td>
                  <td className="px-4 py-2">{user.display_name}</td>
                  <td className="px-4 py-2 text-muted-foreground text-xs">
                    {user.handle ? `@${user.handle}` : '-'}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted font-medium">
                      {user.subscription?.planName || 'Sem plano'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {user.role === 'admin' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                        admin
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-xs text-muted-foreground">
                    {new Date(user.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setDetailUserId(user.id)}>
                      <UserCog className="h-4 w-4" /> Detalhes
                    </Button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    Nenhum usuario encontrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Pagina {page} de {totalPages}
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <AdminUserDetailModal
        userId={detailUserId}
        onClose={() => setDetailUserId(null)}
        onUserChanged={fetchUsers}
      />
    </div>
  );
};

export default AdminUsersTab;
